import { z } from "zod";

export const PASSWORD_MIN_LENGTH = 8;

export type PasswordRuleKey =
  | "length"
  | "uppercase"
  | "lowercase"
  | "number"
  | "special";

export interface PasswordRule {
  key: PasswordRuleKey;
  /** Shown in the requirements checklist. */
  label: string;
  /** Shown as the form error when this is the first unmet rule. */
  error: string;
  test: (value: string) => boolean;
}

/**
 * The single source of truth for password strength across sign-up, team
 * invites, password recovery and the settings password tab. The same list is
 * mirrored in the `accept-invite` and `admin-create-user` edge functions, which
 * re-check it server side.
 */
export const PASSWORD_RULES: PasswordRule[] = [
  {
    key: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
    test: (v) => v.length >= PASSWORD_MIN_LENGTH,
  },
  {
    key: "uppercase",
    label: "One uppercase letter (A–Z)",
    error: "Password must include an uppercase letter",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    key: "lowercase",
    label: "One lowercase letter (a–z)",
    error: "Password must include a lowercase letter",
    test: (v) => /[a-z]/.test(v),
  },
  {
    key: "number",
    label: "One number (0–9)",
    error: "Password must include a number",
    test: (v) => /\d/.test(v),
  },
  {
    key: "special",
    label: "One special character (!@#$…)",
    error: "Password must include a special character",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

export interface PasswordRuleResult extends PasswordRule {
  met: boolean;
}

export function checkPassword(value: string): PasswordRuleResult[] {
  return PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(value) }));
}

export function isStrongPassword(value: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(value));
}

/** The message to surface first, so the user fixes one thing at a time. */
export function firstPasswordError(value: string): string | null {
  return PASSWORD_RULES.find((rule) => !rule.test(value))?.error ?? null;
}

export type PasswordStrengthLevel = {
  /** How many of the rules are satisfied, 0–5. */
  score: number;
  label: string;
  /** Tailwind class for the filled meter segments. */
  barClass: string;
  textClass: string;
};

const STRENGTH_LABELS: Omit<PasswordStrengthLevel, "score">[] = [
  { label: "Too short", barClass: "bg-border", textClass: "text-muted-foreground" },
  { label: "Very weak", barClass: "bg-destructive", textClass: "text-destructive" },
  { label: "Weak", barClass: "bg-destructive", textClass: "text-destructive" },
  { label: "Fair", barClass: "bg-warning", textClass: "text-warning" },
  { label: "Good", barClass: "bg-primary", textClass: "text-primary" },
  { label: "Strong", barClass: "bg-success", textClass: "text-success" },
];

export function passwordStrength(value: string): PasswordStrengthLevel {
  const score = value ? PASSWORD_RULES.filter((rule) => rule.test(value)).length : 0;
  return { score, ...STRENGTH_LABELS[score] };
}

const GENERATOR_SETS = [
  "ABCDEFGHJKLMNPQRSTUVWXYZ",
  "abcdefghijkmnopqrstuvwxyz",
  "23456789",
  "!@#$%^&*?-_",
];

/** Random password that satisfies every rule — used for admin-issued temporary passwords. */
export function generatePassword(length = 16): string {
  const all = GENERATOR_SETS.join("");
  const pick = (chars: string) => {
    const [n] = crypto.getRandomValues(new Uint32Array(1));
    return chars[n % chars.length];
  };

  const chars = GENERATOR_SETS.map(pick);
  while (chars.length < Math.max(length, PASSWORD_MIN_LENGTH)) {
    chars.push(pick(all));
  }

  // Shuffle so the guaranteed characters aren't always in set order.
  for (let i = chars.length - 1; i > 0; i--) {
    const [n] = crypto.getRandomValues(new Uint32Array(1));
    const j = n % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

/**
 * Zod field for anywhere a password is being *set*. Reports one rule at a time
 * in checklist order rather than dumping every failure at once.
 */
export const strongPasswordSchema = z.string().superRefine((value, ctx) => {
  const failed = PASSWORD_RULES.find((rule) => !rule.test(value));
  if (failed) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: failed.error });
  }
});
